import { useState } from 'react';
import {
  Button, Chip, IconButton, Stack, TextField, MenuItem, Tooltip, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, OutlinedInput, Box, FormControl, InputLabel,
} from '@mui/material';
import { Add, Visibility } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { RfqDetailDialog } from './RfqDetailDialog';
import { rfqApi, prApi, type Rfq } from '../../api/procurement.api';
import { vendorsApi } from '../../api/vendors.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

export function RfqListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [prId, setPrId] = useState<number | ''>('');
  const [title, setTitle] = useState('');
  const [vendorIds, setVendorIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['rfqs', page], queryFn: () => rfqApi.list({ page, limit: 25 }) });
  const approvedPrs = useQuery({ queryKey: ['prs', 'approved'], queryFn: () => prApi.list({ limit: 100, status: 'approved' }), enabled: createOpen });
  const vendors = useQuery({ queryKey: ['vendors', 'active'], queryFn: () => vendorsApi.list({ limit: 100 }), enabled: createOpen });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['rfqs'] });

  const createMut = useMutation({
    mutationFn: () => rfqApi.create({ prId: Number(prId), title, vendorIds }),
    onSuccess: () => { setCreateOpen(false); setPrId(''); setTitle(''); setVendorIds([]); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });

  const columns: Column<Rfq>[] = [
    { key: 'code', label: 'RFQ No.', width: 150 },
    { key: 'title', label: 'Title' },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={r.status === 'awarded' ? 'success' : 'info'} /> },
    { key: 'selectionStatus', label: 'Selection', render: (r) => r.selectionStatus ?? '—' },
  ];

  return (
    <>
      <PageHeader title="Request for Quotation (RFQ)" action={hasPermission('rfq.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setCreateOpen(true); setError(null); }}>New RFQ</Button>} />
      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={25} total={data?.meta?.total ?? 0} onPageChange={setPage} onLimitChange={() => undefined} rowKey={(r) => r.id}
        actions={(r) => <Tooltip title="Open"><IconButton size="small" onClick={() => setDetailId(r.id)}><Visibility fontSize="small" /></IconButton></Tooltip>}
      />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New RFQ</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField select label="Approved PR" value={prId} onChange={(e) => setPrId(Number(e.target.value))} fullWidth>
              {(approvedPrs.data?.data ?? []).map((p) => <MenuItem key={p.id} value={p.id}>{p.code} — {p.title}</MenuItem>)}
            </TextField>
            <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Invite Vendors</InputLabel>
              <Select multiple value={vendorIds} input={<OutlinedInput label="Invite Vendors" />} onChange={(e) => setVendorIds(e.target.value as number[])}
                renderValue={(sel) => <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>{(sel as number[]).map((id) => <Chip key={id} size="small" label={vendors.data?.data.find((v) => v.id === id)?.vendorName ?? id} />)}</Box>}>
                {(vendors.data?.data ?? []).map((v) => <MenuItem key={v.id} value={v.id}>{v.vendorName}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!prId || !title || vendorIds.length === 0} onClick={() => createMut.mutate()}>Create</Button>
        </DialogActions>
      </Dialog>

      <RfqDetailDialog rfqId={detailId} onClose={() => { setDetailId(null); invalidate(); }} />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
